import * as THREE from 'three';
import { REDUCE_FRAG, SIM_VERT } from './shaders';
import { makeFloatTarget } from './gpuUtils';

/**
 * 2×2 hierarchical reduction of a float texture down to 1×1.
 * mode 0: count texels with .w >= 0 (live grains)
 * mode 1: sum .r (intermediate counts; also used after the first pass)
 * mode 2: sum .r + .a (heightfield visual mass = granular + film)
 */
export function createReducer(renderer: THREE.WebGLRenderer, maxSize: number) {
  const bySize = new Map<number, THREE.WebGLRenderTarget>();
  let s = maxSize;
  while (s > 1) {
    s = Math.max(1, Math.ceil(s / 2));
    if (!bySize.has(s)) bySize.set(s, makeFloatTarget(s, THREE.NearestFilter));
  }
  const prev = renderer.getRenderTarget();
  for (const lv of bySize.values()) renderer.setRenderTarget(lv);
  renderer.setRenderTarget(prev);

  const uniforms = {
    tSrc: { value: null as THREE.Texture | null },
    uSrcSize: { value: new THREE.Vector2(maxSize, maxSize) },
    uMode: { value: 0 },
  };
  const mat = new THREE.ShaderMaterial({
    uniforms,
    vertexShader: SIM_VERT,
    fragmentShader: REDUCE_FRAG,
    depthTest: false,
    depthWrite: false,
  });
  const scene = new THREE.Scene();
  const cam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  const mesh = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), mat);
  scene.add(mesh);
  const pixel = new Float32Array(4);

  const run = (
    gl: THREE.WebGLRenderer,
    src: THREE.Texture,
    srcSize: number,
    mode: 0 | 1 | 2,
  ): number => {
    let input: THREE.Texture = src;
    let inSize = srcSize;
    const prevTarget = gl.getRenderTarget();
    const prevAuto = gl.autoClear;
    gl.autoClear = true;
    uniforms.uMode.value = mode;
    let last: THREE.WebGLRenderTarget | null = null;
    let guard = 0;
    let passMode: 0 | 1 | 2 = mode;
    while (inSize > 1 && guard < 16) {
      const destSize = Math.max(1, Math.ceil(inSize / 2));
      const dest = bySize.get(destSize);
      if (!dest) break;
      uniforms.tSrc.value = input;
      uniforms.uSrcSize.value.set(inSize, inSize);
      // First pass of a live-count: tally w>=0. Every later pass sums .r
      // (intermediates store the count in R and 1.0 in A).
      uniforms.uMode.value = passMode;
      gl.setRenderTarget(dest);
      gl.render(scene, cam);
      input = dest.texture;
      inSize = dest.width;
      last = dest;
      passMode = 1;
      guard += 1;
    }
    if (!last) {
      gl.setRenderTarget(prevTarget);
      gl.autoClear = prevAuto;
      return 0;
    }
    gl.readRenderTargetPixels(last, 0, 0, 1, 1, pixel);
    gl.setRenderTarget(prevTarget);
    gl.autoClear = prevAuto;
    return pixel[0] || 0;
  };

  return {
    countAlive(gl: THREE.WebGLRenderer, posTex: THREE.Texture, size: number): number {
      return Math.round(run(gl, posTex, size, 0));
    },
    sumRed(gl: THREE.WebGLRenderer, tex: THREE.Texture, size: number): number {
      return run(gl, tex, size, 1);
    },
    sumDeposit(gl: THREE.WebGLRenderer, tex: THREE.Texture, size: number): number {
      return run(gl, tex, size, 2);
    },
    dispose() {
      for (const lv of bySize.values()) lv.dispose();
      mat.dispose();
      mesh.geometry.dispose();
    },
  };
}
